import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import AppointmentCard from "@/pages/appointments/components/AppointmentCard"
import {
  useFetchAppointments,
  useDeleteAppointment,
  useUpdateAppointmentStatus,
  useUpdateAppointment,
  useCompleteAppointment,
} from "@/services/query/appointments.query"
import { useState, useCallback, Suspense, memo } from "react"
import { useToast } from "@/components/ui/use-toast"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"
import { EditAppointmentDialog } from "@/components/appointments/EditAppointmentDialog"
import DateRangePicker from "@/components/shared/date-range-picker/date-range-picker"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { CompleteAppointmentDialog } from "@/components/appointments/CompleteAppointmentDialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const SearchInput = memo(({ onSearch }) => (
  <Input
    type="search"
    placeholder="Search appointments..."
    onChange={(e) => onSearch(e.target.value)}
  />
))

function AppointmentsPage() {
  const { toast } = useToast()
  const [pagination, setPagination] = useState({ offset: 0, limit: 9 })
  const [search, setSearch] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [appointmentStatus, setAppointmentStatus] = useState(null)
  const [editingAppointment, setEditingAppointment] = useState(null)
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  })
  const [dateError, setDateError] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [completingAppointment, setCompletingAppointment] = useState(null)

  // Debounce search
  const onSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }, 500),
    []
  )

  const { data, isLoading, isError, error } = useFetchAppointments(
    pagination.offset,
    pagination.limit,
    search,
    appointmentStatus,
    dateRange?.from ? format(dateRange.from, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null,
    dateRange?.to ? format(dateRange.to, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null
  )

  const deleteAppointmentMutation = useDeleteAppointment()
  const updateStatusMutation = useUpdateAppointmentStatus()
  const updateAppointmentMutation = useUpdateAppointment()
  const completeAppointmentMutation = useCompleteAppointment()

  const handleDelete = async () => {
    if (!selectedAppointment) return

    try {
      await deleteAppointmentMutation.mutateAsync(selectedAppointment.id)
      toast({
        title: "Success",
        description: "Appointment deleted successfully",
      })
      setDeleteDialogOpen(false)
      setSelectedAppointment(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete appointment",
        variant: "destructive",
      })
    }
  }

  const handleStatusUpdate = async (appointment, newStatus) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: appointment.id,
        status: newStatus === 1 ? "APPROVED" : "REJECTED",
        doctor_id: appointment.doctor_id, // preserve the doctor_id when updating status
      })
      toast({
        title: "Success",
        description: `Appointment ${newStatus === 1 ? "accepted" : "rejected"} successfully`,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${newStatus === 1 ? "accept" : "reject"} appointment`,
        variant: "destructive",
      })
    }
  }

  const handleEdit = async (data) => {
    try {
      const updateData = {
        id: editingAppointment.id,
        doctor_id: editingAppointment.doctor_id,
        symptoms: data.symptoms,
        reason: data.reason,
        addition_notes: data.addition_notes,
        appointment_date: data.appointment_date,
        appointment_status: data.appointment_status,
      }

      // Remove undefined or null values
      Object.keys(updateData).forEach(
        (key) => updateData[key] == null && delete updateData[key]
      )

      await updateAppointmentMutation.mutateAsync(updateData)
      toast({
        title: "Success",
        description: "Appointment updated successfully",
      })
      setEditingAppointment(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update appointment",
        variant: "destructive",
      })
    }
  }

  const handleComplete = async (formData) => {
    try {
      if (!completingAppointment) return

      console.log("Completing appointment:", completingAppointment.id) // Add logging

      await completeAppointmentMutation.mutateAsync({
        id: completingAppointment.id,
        formData, // Pass formData directly
      })

      toast({
        title: "Success",
        description: "Appointment marked as completed",
      })
      setCompletingAppointment(null)
    } catch (error) {
      console.error("Complete appointment error:", error) // Add error logging
      toast({
        title: "Error",
        description: "Failed to complete appointment",
        variant: "destructive",
      })
    }
  }

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }))
  }

  const handleDateRangeChange = (newDateRange) => {
    setDateError("")
    if (!newDateRange) {
      setDateRange({ from: null, to: null })
      return
    }
    setDateRange(newDateRange)
  }

  const clearFilters = () => {
    setDateRange({ from: null, to: null })
    setDateError("")
    setAppointmentStatus(null)
    setSearch("")
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="space-y-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="w-full sm:w-auto sm:flex-1 max-w-sm">
              <SearchInput onSearch={onSearch} />
            </div>
            <Select
              value={appointmentStatus || "ALL"}
              onValueChange={(value) =>
                setAppointmentStatus(value === "ALL" ? null : value)
              }
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">
                  <div className="flex items-center">
                    <span className="h-2 w-2 rounded-full bg-gray-400 mr-2" />
                    All Status
                  </div>
                </SelectItem>
                <SelectItem value="PENDING" className="text-yellow-600">
                  <div className="flex items-center">
                    <span className="h-2 w-2 rounded-full bg-yellow-400 mr-2" />
                    Pending
                  </div>
                </SelectItem>
                <SelectItem value="APPROVED" className="text-green-600">
                  <div className="flex items-center">
                    <span className="h-2 w-2 rounded-full bg-green-400 mr-2" />
                    Approved
                  </div>
                </SelectItem>
                <SelectItem value="REJECTED" className="text-red-600">
                  <div className="flex items-center">
                    <span className="h-2 w-2 rounded-full bg-red-400 mr-2" />
                    Rejected
                  </div>
                </SelectItem>
                <SelectItem value="COMPLETED" className="text-blue-600">
                  <div className="flex items-center">
                    <span className="h-2 w-2 rounded-full bg-blue-400 mr-2" />
                    Completed
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DateRangePicker
            date={dateRange}
            onDateChange={handleDateRangeChange}
            error={dateError}
            className="w-full sm:w-[300px]"
          />
        </div>

        {(dateRange.from || dateRange.to || appointmentStatus) && (
          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div className="flex flex-wrap gap-2 text-sm">
              {(dateRange.from || dateRange.to) && (
                <span className="text-gray-600 dark:text-gray-400">
                  Date:{" "}
                  {dateRange.from ? format(dateRange.from, "MMM d, yyyy") : ""}
                  {dateRange.to
                    ? ` - ${format(dateRange.to, "MMM d, yyyy")}`
                    : ""}
                </span>
              )}
              {appointmentStatus && (
                <span
                  className={`font-medium ${getStatusColor(appointmentStatus)}`}
                >
                  Status:{" "}
                  {appointmentStatus.charAt(0) +
                    appointmentStatus.slice(1).toLowerCase()}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {isLoading ? (
          Array(3)
            .fill(0)
            .map((_, idx) => (
              <div key={idx} className="animate-pulse">
                <div className="h-[280px] bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              </div>
            ))
        ) : isError ? (
          <div className="col-span-full p-4 text-center bg-red-50 dark:bg-red-900/10 rounded-lg">
            <p className="text-red-600 dark:text-red-400">
              Error: {error.message}
            </p>
          </div>
        ) : data?.appointments?.length === 0 ? (
          <div className="col-span-full p-8 text-center bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <p className="text-gray-600 dark:text-gray-400">
              No appointments found
            </p>
          </div>
        ) : (
          data?.appointments?.map((appointment) => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              onAccept={() => handleStatusUpdate(appointment, 1)}
              onReject={() => handleStatusUpdate(appointment, 2)}
              onDelete={() => {
                setSelectedAppointment(appointment)
                setDeleteDialogOpen(true)
              }}
              onEdit={() => setEditingAppointment(appointment)}
              onComplete={() => setCompletingAppointment(appointment)}
            />
          ))
        )}
      </div>

      {data && data.pagination.totalPages > 1 && (
        <div className="mt-8 flex justify-center overflow-x-auto">
          <Pagination className="py-2">
            <PaginationContent className="flex-wrap gap-2">
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    handlePageChange(
                      Math.max(0, pagination.offset - pagination.limit)
                    )
                  }
                  disabled={pagination.offset === 0}
                />
              </PaginationItem>
              {[...Array(data.pagination.totalPages)].map((_, idx) => (
                <PaginationItem key={idx}>
                  <PaginationLink
                    onClick={() => handlePageChange(idx * pagination.limit)}
                    isActive={pagination.offset === idx * pagination.limit}
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    handlePageChange(pagination.offset + pagination.limit)
                  }
                  disabled={
                    pagination.offset + pagination.limit >=
                    data.pagination.total
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {editingAppointment && (
        <EditAppointmentDialog
          appointment={editingAppointment}
          open={!!editingAppointment}
          onOpenChange={(open) => !open && setEditingAppointment(null)}
          onSave={handleEdit}
        />
      )}

      {completingAppointment && (
        <CompleteAppointmentDialog
          appointment={completingAppointment}
          open={!!completingAppointment}
          onOpenChange={(open) => !open && setCompletingAppointment(null)}
          onComplete={handleComplete}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              appointment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Add this helper function
function getStatusColor(status) {
  const colors = {
    PENDING: "bg-blue-500 text-white hover:bg-blue-600",
    APPROVED: "bg-green-500 text-white hover:bg-green-600",
    REJECTED: "bg-red-500 text-white hover:bg-red-600",
    COMPLETED: "bg-purple-500 text-white hover:bg-purple-600",
    CANCELED: "bg-gray-500 text-white hover:bg-gray-600",
  }
  return colors[status] || "bg-gray-500 text-white hover:bg-gray-600"
}

export default AppointmentsPage
